import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLang } from '../lib/lang';
import { getAdminChatConversations, adminGetVisitorsLiveCount } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  LayoutDashboard, Trophy, Users, Package, Settings, ArrowLeft, Tag, BarChart3, Image,
  MessageCircle, Crown, RotateCw, Mail, Sparkles, HardDrive, Award, Scale, Pencil,
  CreditCard, Phone, TrendingUp, Bitcoin, Menu, X, AlertTriangle, Eye,
} from 'lucide-react';
import { useBranding } from '../lib/branding';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

const LOGO_URL_DEFAULT = '/assets/winluxor-logo.png';

export default function AdminSidebar() {
  const { t } = useLang();
  const { user } = useAuth();
  const location = useLocation();
  const branding = useBranding();
  const LOGO_URL = branding.logo_main_url || LOGO_URL_DEFAULT;
  const [unreadChats, setUnreadChats] = useState(0);
  const [liveVisitors, setLiveVisitors] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    const loadUnread = () => {
      getAdminChatConversations().then(res => {
        const total = (res.data.conversations || []).reduce((s, c) => s + (c.unread || 0), 0);
        setUnreadChats(total);
      }).catch(() => {});
    };
    const loadVisitors = () => {
      adminGetVisitorsLiveCount().then(res => {
        setLiveVisitors(res.data?.count || 0);
      }).catch(() => {});
    };
    loadUnread();
    loadVisitors();
    const interval = setInterval(() => { loadUnread(); loadVisitors(); }, 15000);
    return () => clearInterval(interval);
  }, [user]);

  // Close drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const navItems = [
    { label: t('dashboard'), icon: LayoutDashboard, path: '/admin' },
    { label: t('salesDashboard') || 'Ventes', icon: TrendingUp, path: '/admin/sales' },
    { label: 'Visiteurs', icon: Eye, path: '/admin/visitors', badge: liveVisitors, badgeColor: 'live' },
    { label: t('raffles'), icon: Trophy, path: '/admin/raffles' },
    { label: t('winners'), icon: Award, path: '/admin/winners' },
    { label: t('users'), icon: Users, path: '/admin/users' },
    { label: 'WhatsApp', icon: Phone, path: '/admin/whatsapp-contacts' },
    { label: t('pointPacks') || 'Packs', icon: Package, path: '/admin/packs' },
    { label: t('promos'), icon: Tag, path: '/admin/promos' },
    { label: t('analytics'), icon: BarChart3, path: '/admin/analytics' },
    { label: t('banners') || 'Banners', icon: Image, path: '/admin/banners' },
    { label: t('chat') || 'Chat', icon: MessageCircle, path: '/admin/chat', badge: unreadChats },
    { label: t('vipPlans'), icon: Crown, path: '/admin/vip' },
    { label: t('spinWheel'), icon: RotateCw, path: '/admin/spin' },
    { label: t('newsletter'), icon: Mail, path: '/admin/newsletter' },
    { label: t('payments') || 'Paiements', icon: CreditCard, path: '/admin/payments' },
    { label: 'Crypto', icon: Bitcoin, path: '/admin/crypto' },
    { label: t('legalPages') || 'Legal Pages', icon: Scale, path: '/admin/legal-pages' },
    { label: t('siteTexts') || 'Site Texts', icon: Pencil, path: '/admin/text-overrides' },
    { label: t('backups') || 'Backups', icon: HardDrive, path: '/admin/backup' },
    { label: 'Erreurs client', icon: AlertTriangle, path: '/admin/client-errors' },
    { label: t('settings'), icon: Settings, path: '/admin/settings' },
  ];

  // Shared nav list (used by both desktop sidebar and mobile drawer)
  const NavList = ({ onItemClick }) => (
    <nav className="px-2 flex-1 space-y-0.5">
      {navItems.map((item) => {
        const isActive = location.pathname === item.path;
        return (
          <Link
            key={item.path}
            to={item.path}
            onClick={onItemClick}
            data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s/g, '-')}-link`}
          >
            <Button
              variant="ghost"
              style={isActive ? { color: 'hsl(var(--gold))' } : { color: 'hsl(43 30% 78%)' }}
              className={`w-full justify-start gap-3 relative h-10 text-[13px] font-medium tracking-wide rounded-lg transition-all
                ${isActive
                  ? 'bg-[hsl(var(--gold)/0.12)] hover:bg-[hsl(var(--gold)/0.16)] border border-[hsl(var(--gold)/0.30)]'
                  : 'hover:bg-[hsl(var(--gold)/0.08)] hover:!text-gold border border-transparent'}`}
            >
              <item.icon className="w-4 h-4 shrink-0" />
              <span className="truncate">{item.label}</span>
              {item.badge > 0 && (
                item.badgeColor === 'live' ? (
                  <Badge className="ml-auto bg-emerald-500 text-white border-none text-[10px] min-w-[20px] h-5 flex items-center justify-center px-1.5 font-mono gap-1">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
                    </span>
                    {item.badge}
                  </Badge>
                ) : (
                  <Badge className="ml-auto bg-red-500 text-white border-none text-[10px] min-w-[20px] h-5 flex items-center justify-center px-1.5 font-mono">
                    {item.badge}
                  </Badge>
                )
              )}
            </Button>
          </Link>
        );
      })}
    </nav>
  );

  // Sidebar content shared between desktop & mobile (logo, header, nav, footer)
  const SidebarBody = ({ onItemClick }) => (
    <>
      {/* Logo */}
      <div className="px-4 py-5 border-b border-[hsl(var(--gold)/0.20)] flex items-center justify-center shrink-0">
        <Link to="/" onClick={onItemClick} className="transition-transform hover:scale-[1.03]">
          <img src={LOGO_URL} alt="WINLUXOR" className="h-14 w-auto rounded-md shadow-[0_4px_16px_rgba(0,0,0,0.3)]" />
        </Link>
      </div>

      {/* Header */}
      <div className="px-4 pt-5 pb-3 shrink-0">
        <p className="gold-line mb-3"><Sparkles className="w-3 h-3" /> {t('adminPanel')}</p>
        <Link to="/" onClick={onItemClick}>
          <Button variant="ghost" size="sm" className="gap-2 text-cream/60 hover:text-gold hover:bg-[hsl(var(--gold)/0.08)] w-full justify-start text-xs">
            <ArrowLeft className="w-3.5 h-3.5" />
            {t('backToSite')}
          </Button>
        </Link>
      </div>

      <div className="px-2">
        <div className="divider-gold mb-2" />
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto pb-4">
        <NavList onItemClick={onItemClick} />
      </div>

      {/* Footer */}
      <div className="px-4 py-4 border-t border-[hsl(var(--gold)/0.18)] shrink-0">
        <p className="text-[10px] text-cream/40 uppercase tracking-[0.2em] text-center font-semibold">
          © WINLUXOR — Admin
        </p>
      </div>
    </>
  );

  return (
    <>
      {/* ── Desktop sidebar (lg+) ─────────────────────────────── */}
      <aside
        className="w-64 min-h-screen bg-rolex-green border-r border-[hsl(var(--gold)/0.20)] hidden lg:flex flex-col sticky top-0 h-screen overflow-hidden"
        style={{ color: 'hsl(43 38% 92%)' }}
        data-testid="admin-sidebar"
        translate="no"
      >
        <SidebarBody />
      </aside>

      {/* ── Mobile hamburger (< lg) ───────────────────────────── */}
      <div
        className="lg:hidden fixed top-3 left-3 z-50"
        data-testid="admin-mobile-menu-trigger-wrapper"
        translate="no"
      >
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button
              size="icon"
              className="w-11 h-11 rounded-xl bg-rolex-green text-cream border border-[hsl(var(--gold)/0.40)] shadow-lg shadow-rolex-green/30 hover:bg-rolex-green-elev"
              data-testid="admin-mobile-menu-btn"
              aria-label="Ouvrir le menu admin"
            >
              <Menu className="w-5 h-5 text-gold" />
            </Button>
          </SheetTrigger>
          <SheetContent
            side="left"
            className="w-72 p-0 bg-rolex-green border-r border-[hsl(var(--gold)/0.30)] flex flex-col"
            style={{ color: 'hsl(43 38% 92%)' }}
            data-testid="admin-mobile-drawer"
            translate="no"
          >
            <SidebarBody onItemClick={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
}
